/**
 * Live-trading direct cancel — the per-order resting chip on ladder rows.
 *
 * One chip = one resting Kalshi order (`BidRowOrder` carries its orderId).
 * Clicking the chip cancels that order without leaving the ladder:
 *
 *   • Cancel guard ON (`confirmOrderCancel`, the default) — first click arms
 *     the chip ("✕ 100?"), second click within the arm window cancels. The
 *     arm auto-expires so a stray click can't leave a live cancel trap.
 *   • Cancel guard set to "One click" — a single click cancels immediately.
 *
 * While the shared #241 cancel fence is pending the chip dims and ignores
 * clicks; a timed-out fence renders amber with the truthful sync message and
 * a click retries. A rejected DELETE keeps the chip visible and wears the
 * error in its tooltip — the order is still live and must not disappear.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { queueTone } from '@/components/cards/orderCardModel';
import { useAccountStore } from '@/stores/accountStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { isKalshiCancelPending } from '@/lib/trading/kalshiCancelLifecycle';
import { formatVolume, type BidRowOrder } from './helpers';

/** Auto-disarm window for the two-step confirm (ms). Short on purpose: live
 *  ladders move, and a minutes-old armed chip is a mis-cancel waiting. */
const ARM_TIMEOUT_MS = 2_500;
/** How long a failed cancel wears its error tooltip before reverting. */
const ERROR_TTL_MS = 4_000;

export function RestingOrderChip({ order }: { order: BidRowOrder }) {
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  // Narrow per-order selector — lifecycle helpers return the map by identity
  // on no-op transitions, so quiet polls never re-render the chips.
  const cancelState = useAccountStore((s) => s.kalshiCancelStates[order.orderId]);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  const [armed, setArmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (armTimer.current) clearTimeout(armTimer.current);
      if (errorTimer.current) clearTimeout(errorTimer.current);
    },
    [],
  );

  const pending = isKalshiCancelPending(cancelState);
  const timedOut = cancelState?.phase === 'timed-out';

  const disarm = () => {
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = null;
    setArmed(false);
  };

  const fireCancel = () => {
    disarm();
    cancelOrder(order.orderId).catch((err: unknown) => {
      setError(err instanceof Error && err.message ? err.message : 'Cancel failed');
      if (errorTimer.current) clearTimeout(errorTimer.current);
      errorTimer.current = setTimeout(() => setError(null), ERROR_TTL_MS);
    });
  };

  /** The one decision, shared by pointer and keyboard so the guard cannot
   *  differ between them — a one-press keyboard cancel on a surface whose
   *  pointer path arms first would be a quieter version of the bug the arm
   *  exists to prevent. */
  const activate = () => {
    if (pending) return;
    if (confirmOrderCancel && !armed) {
      setArmed(true);
      if (armTimer.current) clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => setArmed(false), ARM_TIMEOUT_MS);
      return;
    }
    fireCancel();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    activate();
  };

  /** #650 — the chip was `role="button"` with no `tabIndex` and no key
   *  handler, so it announced itself as a control and then could not be
   *  reached or operated by one. Enter and Space are the two keys the button
   *  role contracts for; Space must also preventDefault or the ladder's
   *  scroll container pages down under the trader. */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation();
    activate();
  };

  const qty = Math.round(order.remainingCount);
  const title = pending
    ? `Canceling ${qty} @ order ${order.orderId}…`
    : timedOut
      ? cancelState?.message ?? 'Cancel unconfirmed — click to retry'
      : error
        ? `${error} — order still live`
        : armed
          ? `Click again to cancel ${qty} resting`
          : `${order.tooltip} — click to cancel`;

  return (
    <button
      // ⚠️ A REAL <button>, and that is the safety fence — not a style choice.
      //
      // `Lt2HotkeyListener.ownedByInteractive` decides whether a keystroke
      // belongs to a control via `el.closest('button')`; `role="button"` is
      // deliberately absent from its selector list (see lt2FocusRegion.ts,
      // which documents the divergence). While these chips were unfocusable
      // that gap was harmless — they could never own focus. #650 made them
      // focusable, which turned it live: a trader tabbed onto a CANCEL control
      // could press A/D and place an order from it.
      //
      // As a real button the fence sees it and refuses A/D. The chip sits in
      // its own LadderRow <tr>, outside the `[data-lt2-hotkey-control]`
      // boundary painted on the team row, so the LT2 hand-back exemption does
      // not apply here. `aria-disabled` rather than `disabled` while pending:
      // the order still exists and must stay reachable and announced.
      type="button"
      aria-disabled={pending || undefined}
      title={title}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseLeave={armed ? disarm : undefined}
      className={cn(
        'truncate rounded px-0.5 transition-colors',
        // #650 — `text-[10px]` is an arbitrary Tailwind value, so it sets the
        // font size and NOTHING else: the line box stayed at the inherited
        // 20px while the row's own text sits in a 16px one. A ladder row was
        // therefore 24px tall the moment it carried a chip and 20px when it
        // did not, so the card grew and shrank by 4px as orders arrived and
        // left — measured at all three viewports. Pinning the leading is the
        // height-axis form of the constant-width rule #615 applied to this
        // same chip, and it is what lets an order arrive without moving
        // anything.
        'leading-4',
        'focus-visible:ring-primary/70 focus-visible:outline-none focus-visible:ring-2',
        pending
          ? 'animate-pulse cursor-default opacity-50'
          : 'cursor-pointer hover:bg-negative/15',
        armed && 'bg-negative/20 text-negative',
        timedOut && 'text-amber-400',
        error && 'text-negative',
      )}
    >
      {armed ? (
        <span className="font-semibold">✕ {qty}?</span>
      ) : (
        <>
          <span>{qty}/</span>
          {/* #615 — constant-width live numeric cell, the same treatment the
              table header comment prescribes and AggregateVolumeCell already
              uses for this exact formatter. Kalshi legitimately reports a
              queue that swings between magnitudes for an UNCHANGED order
              (#240), so on each snapshot poll `formatVolume` can alternate
              between "700.0K" and "9,000" — measured at 6.03px of width, which
              moved this cell's neighbours sideways. The box is now fixed, so
              the column's max-content cannot move between readings. The VALUE
              is untouched: #240 forbids smoothing it, and nothing here rounds,
              clamps or delays. */}
          <span
            className={cn(
              'inline-block w-[6ch] text-right tabular-nums',
              queueTone(order.queueOrBetter, null),
            )}
          >
            {order.queueOrBetter != null ? formatVolume(Math.round(order.queueOrBetter)) : '?'}
          </span>
        </>
      )}
    </button>
  );
}
